import React, { useEffect, useMemo, useState } from "react";
import { formatOrganizationSyncStatusLine } from "../utils/dates";
import { jsonHeaders } from "../utils/jsonHeaders";
import { normalizeResponseErrors } from "../utils/apiErrors";

const ENQUEUE_SUCCESS_MESSAGE = {
  people: "People are syncing, refresh the page to see the updated results.",
  devices: "Devices are syncing, refresh the page to see the updated results.",
  billing: "Billing data is syncing, refresh the page to see the updated results.",
  tickets: "Tickets are syncing, refresh the page to see the updated results.",
  "support analytics":
    "Support analytics is refreshing, refresh the page to see the updated results.",
};

const ENQUEUE_ERROR_MESSAGE = "Sync failed: try again later";

const DEFAULT_DISABLED_BUTTON_TITLE = "Sync unavailable. Please try again later.";

export default function OrganizationSyncToolbar({
  organizationSync: initial,
  staleAfterHours,
}) {
  const resource = initial?.resource;
  const resourceLabel = initial?.resourceLabel;
  const initialResolvedButtonLabel = initial?.resolvedButtonLabel;
  const createUrl = initial?.createUrl;

  const [syncDisabled, setSyncDisabled] = useState(
    initial?.syncDisabled !== undefined ? Boolean(initial?.syncDisabled) : true
  );
  const [disabledButtonTitle, setDisabledButtonTitle] = useState(
    initial?.disabledButtonTitle ?? DEFAULT_DISABLED_BUTTON_TITLE
  );
  const [resolvedButtonLabel, setResolvedButtonLabel] = useState(initialResolvedButtonLabel);
  const [lastSyncedAt, setLastSyncedAt] = useState(initial?.lastSyncedAt ?? null);
  const [loading, setLoading] = useState(false);
  const [enqueueSuccessMessage, setEnqueueSuccessMessage] = useState(null);
  const [enqueueErrorMessage, setEnqueueErrorMessage] = useState(null);

  // The component is prerendered server-side in UTC. Rendering the timestamp in
  // UTC until mount keeps SSR and first client paint identical (no hydration
  // mismatch); after mount we re-render in the viewer's local timezone.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const statusLineText = useMemo(
    () => formatOrganizationSyncStatusLine(lastSyncedAt, { utc: !mounted }),
    [lastSyncedAt, mounted]
  );

  // Opt-in staleness flag: when the caller passes a freshness window (e.g. KPI
  // snapshots refresh hourly), warn once the last sync is older than it.
  const isStale = useMemo(() => {
    if (!staleAfterHours || !lastSyncedAt) return false;
    const syncedMs = new Date(lastSyncedAt).getTime();
    if (Number.isNaN(syncedMs)) return false;
    return Date.now() - syncedMs > staleAfterHours * 60 * 60 * 1000;
  }, [lastSyncedAt, staleAfterHours]);

  // Hooks must run unconditionally, so bail out only after they're declared:
  // render nothing until the toolbar has its minimum server-provided props.
  if (!resource || !initialResolvedButtonLabel) return null;

  function applyPayload(data) {
    if (!data) return;
    if ("syncDisabled" in data) {
      setSyncDisabled(Boolean(data.syncDisabled));
    }
    if ("disabledButtonTitle" in data) {
      setDisabledButtonTitle(data.disabledButtonTitle ?? DEFAULT_DISABLED_BUTTON_TITLE);
    }
    if ("resolvedButtonLabel" in data) {
      setResolvedButtonLabel(data.resolvedButtonLabel ?? "");
    }
    if ("lastSyncedAt" in data) {
      setLastSyncedAt(data.lastSyncedAt ?? null);
    }
  }

  const buttonDisabled = syncDisabled || loading;

  async function onSync() {
    if (!createUrl || buttonDisabled) return;
    setEnqueueSuccessMessage(null);
    setEnqueueErrorMessage(null);
    setLoading(true);
    try {
      const res = await fetch(createUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: jsonHeaders(),
        body: JSON.stringify({ resource }),
      });
      const data = await res.json().catch(() => ({}));
      applyPayload(data);
      if (res.ok) {
        const label = data.resourceLabel ?? resourceLabel;
        setEnqueueSuccessMessage(ENQUEUE_SUCCESS_MESSAGE[label]);
      } else {
        // Surface a server-provided message (e.g. the authorization flash on 403)
        // when present; otherwise fall back to the generic sync error.
        setEnqueueErrorMessage(
          normalizeResponseErrors(data, ENQUEUE_ERROR_MESSAGE).join(" "),
        );
      }
    } catch {
      setEnqueueErrorMessage(ENQUEUE_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  }

  const loadingButtonLabel = initial?.loadingButtonLabel ?? "Syncing…";

  return (
    <div className="organization-sync-toolbar">
      <div className="directory-sync-toolbar">
        <button
          type="button"
          className="btn btn--primary"
          disabled={buttonDisabled}
          title={buttonDisabled ? disabledButtonTitle || undefined : undefined}
          onClick={onSync}
        >
          {loading ? loadingButtonLabel : resolvedButtonLabel}
        </button>
        <div className="organization-sync-toolbar__meta text-muted">
          <div
            suppressHydrationWarning
            className={isStale ? "text-[var(--color-warning)]" : undefined}
          >
            {statusLineText}
            {isStale ? " · data may be stale" : ""}
          </div>
        </div>
      </div>
      {enqueueSuccessMessage && (
        <div className="alert alert--success mt-2 mb-0" role="status" aria-live="polite">
          {enqueueSuccessMessage}
        </div>
      )}
      {enqueueErrorMessage && (
        <div className="alert alert--danger mt-2 mb-0" role="alert">
          {enqueueErrorMessage}
        </div>
      )}
    </div>
  );
}
